import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  ListItem,
  Typography,
} from '@mui/material';
import {
  Done,
  DoneAll,
  Person as PersonIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import AttendantAssignment from '../AttendantAssignment';
import ConversationListContactTags, {
  ConversationContactTagBrief,
} from './ConversationListContactTags';
import { getInboxAvatarColors, getInboxInitials } from '../../utils/inboxAvatarColors';
import { formatConversationPreview } from '../../utils/inboxMessagePreview';

export interface InboxConversationListItemData {
  id: string;
  contact?: {
    id?: string;
    name?: string;
    avatar_url?: string;
  };
  last_message?: string;
  last_message_at?: string;
  updated_at?: string;
  unread_count: number;
  contact_tags?: ConversationContactTagBrief[];
  last_message_direction?: 'inbound' | 'outbound';
  last_message_status?: string;
}

interface InboxConversationListItemProps {
  conversation: InboxConversationListItemData;
  selected: boolean;
  mode: 'light' | 'dark';
  isStage4Lead?: boolean;
  formatTime: (dateString: string) => string;
  onSelect: () => void;
}

function MessageStatusIcon({
  direction,
  status,
}: {
  direction?: 'inbound' | 'outbound';
  status?: string;
}) {
  if (direction !== 'outbound') return null;

  const normalized = (status || '').toLowerCase();
  const isRead = normalized === 'read';
  const isDelivered =
    isRead || normalized === 'delivered' || normalized === 'received';

  const color = isRead ? '#53bdeb' : '#94a3b8';
  const Icon = isDelivered ? DoneAll : Done;

  return (
    <Icon
      sx={{
        fontSize: 16,
        color,
        flexShrink: 0,
        ml: 0.25,
      }}
    />
  );
}

const InboxConversationListItem: React.FC<InboxConversationListItemProps> = ({
  conversation,
  selected,
  mode,
  isStage4Lead,
  formatTime,
  onSelect,
}) => {
  const name = conversation.contact?.name || 'Usuário';
  const avatarColors = getInboxAvatarColors(name);
  const hasUnread = conversation.unread_count > 0;
  const timeStr = formatTime(
    conversation.last_message_at || conversation.updated_at || '',
  );
  const preview = formatConversationPreview(conversation.last_message);

  return (
    <ListItem
      component="div"
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        px: 2,
        py: 2.25,
        mx: 1,
        my: 0.75,
        borderRadius: 2.5,
        alignItems: 'flex-start',
        backgroundColor: selected
          ? mode === 'dark'
            ? 'rgba(15, 118, 110, 0.18)'
            : '#f0f9f8'
          : 'transparent',
        borderLeft: selected ? '3px solid #0f766e' : '3px solid transparent',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          backgroundColor: selected
            ? mode === 'dark'
              ? 'rgba(15, 118, 110, 0.22)'
              : '#e8f5f3'
            : mode === 'dark'
              ? 'rgba(255, 255, 255, 0.04)'
              : '#f8fafc',
        },
      }}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        badgeContent={
          hasUnread
            ? conversation.unread_count > 99
              ? '99+'
              : conversation.unread_count
            : undefined
        }
        invisible={!hasUnread}
        sx={{
          mr: 1.75,
          flexShrink: 0,
          '& .MuiBadge-badge': {
            bgcolor: '#25D366',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.65rem',
            minWidth: 18,
            height: 18,
            border: '2px solid #fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Avatar
          src={conversation.contact?.avatar_url}
          sx={{
            width: 48,
            height: 48,
            bgcolor: avatarColors.bg,
            color: avatarColors.color,
            fontSize: '0.9rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            fontFamily: 'inherit',
          }}
        >
          {conversation.contact?.name ? (
            getInboxInitials(conversation.contact.name)
          ) : (
            <PersonIcon sx={{ fontSize: 22 }} />
          )}
        </Avatar>
      </Badge>

      <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.35,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: hasUnread || selected ? 700 : 600,
                fontSize: '0.92rem',
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {name}
            </Typography>
            {isStage4Lead && (
              <StarIcon
                sx={{
                  fontSize: 15,
                  color: '#EAB308',
                  flexShrink: 0,
                }}
              />
            )}
            <AttendantAssignment conversationId={conversation.id} compact />
          </Box>
          {timeStr && (
            <Typography
              variant="caption"
              sx={{
                color: hasUnread ? '#0f766e' : 'text.secondary',
                fontSize: '0.72rem',
                fontWeight: hasUnread ? 600 : 400,
                flexShrink: 0,
                lineHeight: 1.3,
              }}
            >
              {timeStr}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.35,
            mb: conversation.contact_tags?.length ? 0.75 : 0,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              color: hasUnread ? 'text.primary' : 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: hasUnread ? 500 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
              opacity: hasUnread ? 0.92 : 0.85,
            }}
          >
            {preview}
          </Typography>
          <MessageStatusIcon
            direction={conversation.last_message_direction}
            status={conversation.last_message_status}
          />
        </Box>

        <ConversationListContactTags
          tags={conversation.contact_tags}
          variant="row"
        />
      </Box>
    </ListItem>
  );
};

export default InboxConversationListItem;
